import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { startAlarm, stopAlarm, playChime, notify } from "@/lib/sound";
import { tasksApi } from "@/lib/api";

const AlertsContext = createContext(null);

const ALARMS_KEY = "vk_alarms";
const FIRED_KEY = "vk_fired_reminders";

const loadAlarms = () => {
  try {
    return JSON.parse(localStorage.getItem(ALARMS_KEY) || "[]");
  } catch {
    return [];
  }
};
const loadFired = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

export const AlertsProvider = ({ children }) => {
  // --- Ringing state (Wecker / Erinnerung / Timer) ---
  const [ringing, setRinging] = useState(null); // { title, subtitle, kind, snoozeId }

  const ring = (payload) => {
    startAlarm();
    notify(payload.title, payload.subtitle || "");
    setRinging(payload);
  };
  const stopRinging = () => {
    stopAlarm();
    setRinging(null);
  };

  // --- Standalone alarms (Wecker) ---
  const [alarms, setAlarms] = useState(loadAlarms);
  useEffect(() => {
    localStorage.setItem(ALARMS_KEY, JSON.stringify(alarms));
  }, [alarms]);

  const addAlarm = (time, label) =>
    setAlarms((prev) => [
      ...prev,
      { id: crypto.randomUUID(), time, label: label || "", enabled: true, lastFired: null },
    ]);
  const removeAlarm = (id) => setAlarms((prev) => prev.filter((a) => a.id !== id));
  const toggleAlarm = (id) =>
    setAlarms((prev) => prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a)));

  // --- Task reminders ---
  const tasksRef = useRef([]);
  const setTasks = (tasks) => {
    tasksRef.current = tasks;
  };
  const firedRef = useRef(loadFired());
  const reloadRef = useRef(null);
  const setReload = (fn) => {
    reloadRef.current = fn;
  };

  // --- Snooze (aufgeschobene Erinnerungen/Wecker) ---
  const snoozesRef = useRef([]); // [{ payload, at }]

  const snooze = (minutes = 5) => {
    const payload = ringing;
    if (!payload) return;
    const at = Date.now() + minutes * 60 * 1000;
    if (payload.taskId) {
      // Aufgaben-Erinnerung im Backend verschieben (auch für Push) und erneut zulassen
      firedRef.current.delete(payload.taskId);
      localStorage.setItem(FIRED_KEY, JSON.stringify([...firedRef.current]));
      tasksApi
        .update(payload.taskId, { reminder_at: new Date(at).toISOString() })
        .then(() => reloadRef.current && reloadRef.current())
        .catch(() => {});
    } else {
      // Lokal neu einplanen (Wecker)
      snoozesRef.current.push({ payload: { ...payload }, at });
    }
    stopRinging();
  };

  // --- Timer (Pomodoro) ---
  const [duration, setDuration] = useState(25 * 60);
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [label, setLabel] = useState("");
  const endRef = useRef(null);

  const startTimer = (seconds, lbl = "") => {
    const d = seconds ?? duration;
    setDuration(d);
    setLabel(lbl);
    setRemaining(d);
    endRef.current = Date.now() + d * 1000;
    setRunning(true);
  };
  const pauseTimer = () => setRunning(false);
  const resumeTimer = () => {
    endRef.current = Date.now() + remaining * 1000;
    setRunning(true);
  };
  const resetTimer = () => {
    setRunning(false);
    setRemaining(duration);
  };

  // --- Master tick: timer countdown + alarm/reminder checks ---
  useEffect(() => {
    const tick = () => {
      // Timer
      if (running && endRef.current) {
        const rem = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
        setRemaining(rem);
        if (rem <= 0) {
          setRunning(false);
          playChime();
          ring({
            kind: "timer",
            title: "timerFinished",
            subtitle: label || "",
          });
        }
      }

      const now = Date.now();

      // Aufgeschobene (gesnoozte) Erinnerungen/Wecker
      if (snoozesRef.current.length) {
        const due = snoozesRef.current.filter((s) => now >= s.at);
        if (due.length) {
          snoozesRef.current = snoozesRef.current.filter((s) => now < s.at);
          ring(due[0].payload);
        }
      }

      // Standalone alarms (match current HH:MM once per minute)
      const nowHM = new Date().toTimeString().slice(0, 5);
      const minuteKey = new Date().toISOString().slice(0, 16);
      setAlarms((prev) => {
        let changed = false;
        const next = prev.map((a) => {
          if (a.enabled && a.time === nowHM && a.lastFired !== minuteKey) {
            changed = true;
            ring({ kind: "alarm", title: "ringAlarm", subtitle: a.label || "" });
            return { ...a, lastFired: minuteKey };
          }
          return a;
        });
        return changed ? next : prev;
      });

      // Task reminders
      for (const task of tasksRef.current) {
        if (task.reminder_at && !task.completed && !firedRef.current.has(task.id)) {
          const due = new Date(task.reminder_at).getTime();
          if (!isNaN(due) && now >= due && now - due < 24 * 3600 * 1000) {
            firedRef.current.add(task.id);
            localStorage.setItem(FIRED_KEY, JSON.stringify([...firedRef.current]));
            ring({ kind: "reminder", title: "ringReminder", subtitle: task.title, taskId: task.id });
          }
        }
      }
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, label]);

  const value = {
    ringing,
    ring,
    stopRinging,
    snooze,
    alarms,
    addAlarm,
    removeAlarm,
    toggleAlarm,
    setTasks,
    setReload,
    // timer
    duration,
    remaining,
    running,
    timerLabel: label,
    startTimer,
    pauseTimer,
    resumeTimer,
    resetTimer,
    setDuration,
  };

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error("useAlerts must be used within AlertsProvider");
  return ctx;
};
